import { useState } from "react";
import { Fingerprint, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { cancelled, passkeysSupported, unlockWithPasskey } from "@/lib/passkeys";
import { Button } from "@/components/ui/button";

/** Sign in with Face ID / fingerprint. The password form stays right where it is as the fallback. */
export const PasskeyUnlock = ({ scope, email = "", onSignedIn, testId = "passkey-unlock" }) => {
  const { applySession } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!passkeysSupported()) return null;

  const unlock = async () => {
    setBusy(true);
    setError("");
    try {
      const session = await unlockWithPasskey(scope, email);
      applySession(session);
      onSignedIn?.(session);
    } catch (e) {
      if (!cancelled(e)) setError(e?.name === "InvalidStateError"
        ? "No passkey saved on this device yet — sign in with your password once, then add one."
        : errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-ink-600" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">or</span>
        <span className="h-px flex-1 bg-ink-600" />
      </div>
      <Button data-testid={testId} type="button" variant="outline" disabled={busy} onClick={unlock}
        className="h-12 w-full border-ink-600 text-slate-200 hover:border-gold hover:text-gold">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          : <Fingerprint className="mr-2 h-4 w-4" />}
        Unlock with Face ID or fingerprint
      </Button>
      {error && <p data-testid={`${testId}-error`} className="text-sm text-red-400">{error}</p>}
    </div>
  );
};
